import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, XCircle, RotateCcw, Trophy, Brain } from "lucide-react";

interface Question {
	question: string;
	options: string[];
	correctIndex: number;
	explanation: string;
	law: number;
}

const QUESTIONS: Question[] = [
	{
		question:
			"A hockey puck slides across a frictionless ice surface. What happens to its velocity?",
		options: [
			"It gradually slows down",
			"It remains constant",
			"It speeds up over time",
			"It changes direction randomly",
		],
		correctIndex: 1,
		explanation:
			"Newton's First Law: without friction (no net external force), the puck maintains constant velocity indefinitely.",
		law: 1,
	},
	{
		question:
			"Why does a heavier shopping cart require more force to accelerate than a lighter one?",
		options: [
			"Heavier carts have more friction",
			"Heavier carts have more inertia (mass), so F = ma requires more F for the same a",
			"Gravity pulls heavier carts backward",
			"The wheels lock up under heavy loads",
		],
		correctIndex: 1,
		explanation:
			"From F = ma, if you want the same acceleration (a), increasing mass (m) requires proportionally more force (F).",
		law: 2,
	},
	{
		question:
			"A 10 N applied force acts on a 5 kg block on a surface with friction force of 4 N. What is the net force?",
		options: ["10 N", "14 N", "6 N", "4 N"],
		correctIndex: 2,
		explanation:
			"Net force = Applied force - Friction = 10 N - 4 N = 6 N. The net force determines the actual acceleration.",
		law: 2,
	},
	{
		question:
			"When you push against a wall with 50 N of force, how much force does the wall exert on you?",
		options: ["0 N", "25 N", "50 N", "100 N"],
		correctIndex: 2,
		explanation:
			"Newton's Third Law: the wall pushes back on you with exactly 50 N in the opposite direction. Forces always come in equal and opposite pairs.",
		law: 3,
	},
	{
		question:
			"A 2 kg object has a net force of 10 N acting on it. What is its acceleration?",
		options: ["2 m/s²", "5 m/s²", "10 m/s²", "20 m/s²"],
		correctIndex: 1,
		explanation:
			"Using F = ma → a = F/m = 10 N / 2 kg = 5 m/s². The acceleration is directly proportional to force and inversely proportional to mass.",
		law: 2,
	},
	{
		question:
			"A book sits on a table. Which pair are action-reaction forces?",
		options: [
			"The book's weight and the table's normal force on the book",
			"The book's weight (Earth pulling book) and the book pulling Earth upward",
			"Friction and gravity",
			"The table's weight and the floor's support",
		],
		correctIndex: 1,
		explanation:
			"Action-reaction pairs act on DIFFERENT objects. Earth pulls the book down (gravity), and the book pulls Earth up with equal force. The weight/normal force pair act on the same object (the book), so they're not an action-reaction pair -- they're just balanced forces.",
		law: 3,
	},
	{
		question:
			"A passenger in a braking car lurches forward. This is best explained by:",
		options: [
			"A forward force pushes the passenger",
			"The passenger's inertia -- their body tends to stay in motion",
			"Gravity pulls them forward",
			"The seatbelt applies a forward force",
		],
		correctIndex: 1,
		explanation:
			"Newton's First Law: the passenger's body was moving forward with the car. When the car decelerates, the passenger's body resists the change in motion (inertia) and continues moving forward.",
		law: 1,
	},
	{
		question:
			"If you double the net force on an object while keeping mass constant, the acceleration:",
		options: [
			"Stays the same",
			"Doubles",
			"Halves",
			"Quadruples",
		],
		correctIndex: 1,
		explanation:
			"From F = ma: if F doubles and m stays constant, then a must also double. Acceleration is directly proportional to net force.",
		law: 2,
	},
];

export function Quiz() {
	const [currentQ, setCurrentQ] = useState(0);
	const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
	const [score, setScore] = useState(0);
	const [answered, setAnswered] = useState<boolean[]>(
		new Array(QUESTIONS.length).fill(false),
	);
	const [showResult, setShowResult] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	const question = QUESTIONS[currentQ];
	const isCorrect = selectedAnswer === question.correctIndex;

	const handleAnswer = (index: number) => {
		if (showResult) return;
		setSelectedAnswer(index);
		setShowResult(true);
		if (index === question.correctIndex) {
			setScore((s) => s + 1);
		}
		const newAnswered = [...answered];
		newAnswered[currentQ] = true;
		setAnswered(newAnswered);
	};

	const nextQuestion = () => {
		if (currentQ < QUESTIONS.length - 1) {
			setCurrentQ((q) => q + 1);
			setSelectedAnswer(null);
			setShowResult(false);
		} else {
			setIsComplete(true);
		}
	};

	const restart = () => {
		setCurrentQ(0);
		setSelectedAnswer(null);
		setScore(0);
		setAnswered(new Array(QUESTIONS.length).fill(false));
		setShowResult(false);
		setIsComplete(false);
	};

	const percentage = Math.round((score / QUESTIONS.length) * 100);

	if (isComplete) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-3">
					<Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-sm px-3 py-1">
						Quiz
					</Badge>
					<h2 className="text-2xl font-bold">Quiz Complete!</h2>
				</div>

				<Card className="bg-card/50 border-muted">
					<CardContent className="pt-8 pb-8 text-center space-y-6">
						<Trophy
							className={`w-16 h-16 mx-auto ${percentage >= 70 ? "text-yellow-400" : "text-muted-foreground"}`}
						/>
						<div>
							<p className="text-4xl font-bold font-mono">
								{score} / {QUESTIONS.length}
							</p>
							<p className="text-muted-foreground mt-1">
								{percentage}% correct
							</p>
						</div>
						<Progress value={percentage} className="w-64 mx-auto" />
						<p className="text-lg">
							{percentage >= 90
								? "Excellent! You have a strong grasp of Newton's Laws!"
								: percentage >= 70
									? "Good job! You understand the fundamentals well."
									: percentage >= 50
										? "Decent effort! Review the lessons and try again to strengthen your understanding."
										: "Keep studying! Go through the lessons again and pay attention to the interactive demos."}
						</p>
						<Button
							onClick={restart}
							className="flex items-center gap-2 mx-auto"
						>
							<RotateCcw className="w-4 h-4" /> Try Again
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="space-y-3">
				<div className="flex items-center gap-3">
					<Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-sm px-3 py-1">
						Quiz
					</Badge>
					<h2 className="text-2xl font-bold">
						Test Your Knowledge
					</h2>
				</div>
				<p className="text-muted-foreground">
					Answer questions covering all three of Newton's Laws.
				</p>
			</div>

			<div className="flex items-center gap-3">
				<Progress
					value={((currentQ + (showResult ? 1 : 0)) / QUESTIONS.length) * 100}
					className="flex-1"
				/>
				<span className="text-sm text-muted-foreground font-mono">
					{currentQ + 1}/{QUESTIONS.length}
				</span>
			</div>

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-6 space-y-4">
					<div className="flex items-start gap-3">
						<Brain className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
						<div>
							<Badge
								variant="outline"
								className="mb-2 text-xs"
							>
								Law {question.law}
							</Badge>
							<p className="text-lg font-medium">
								{question.question}
							</p>
						</div>
					</div>

					<div className="space-y-2 pt-2">
						{question.options.map((option, i) => {
							let variant:
								| "outline"
								| "default"
								| "secondary"
								| "destructive" = "outline";
							let extraClass = "";

							if (showResult) {
								if (i === question.correctIndex) {
									extraClass =
										"border-green-500 bg-green-500/10 text-green-400";
								} else if (
									i === selectedAnswer &&
									!isCorrect
								) {
									extraClass =
										"border-red-500 bg-red-500/10 text-red-400";
								} else {
									extraClass = "opacity-50";
								}
							} else {
								extraClass =
									"hover:bg-muted/50 hover:border-primary/50 cursor-pointer";
							}

							return (
								<button
									key={i}
									onClick={() => handleAnswer(i)}
									disabled={showResult}
									className={`w-full text-left p-3 rounded-lg border transition-all flex items-center gap-3 ${extraClass}`}
								>
									<span className="w-7 h-7 rounded-full border flex items-center justify-center text-sm font-mono shrink-0">
										{String.fromCharCode(65 + i)}
									</span>
									<span className="text-sm">{option}</span>
									{showResult &&
										i === question.correctIndex && (
											<CheckCircle className="w-5 h-5 text-green-400 ml-auto shrink-0" />
										)}
									{showResult &&
										i === selectedAnswer &&
										!isCorrect && (
											<XCircle className="w-5 h-5 text-red-400 ml-auto shrink-0" />
										)}
								</button>
							);
						})}
					</div>
				</CardContent>
			</Card>

			{showResult && (
				<Card
					className={`${isCorrect ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"}`}
				>
					<CardContent className="pt-4 pb-3 space-y-2">
						<div className="flex items-center gap-2">
							{isCorrect ? (
								<CheckCircle className="w-5 h-5 text-green-400" />
							) : (
								<XCircle className="w-5 h-5 text-red-400" />
							)}
							<span
								className={`font-semibold ${isCorrect ? "text-green-400" : "text-red-400"}`}
							>
								{isCorrect ? "Correct!" : "Not quite."}
							</span>
						</div>
						<p className="text-sm text-muted-foreground">
							{question.explanation}
						</p>
						<Button
							onClick={nextQuestion}
							className="mt-2"
							size="sm"
						>
							{currentQ < QUESTIONS.length - 1
								? "Next Question"
								: "See Results"}
						</Button>
					</CardContent>
				</Card>
			)}

			<div className="flex gap-1.5 justify-center">
				{QUESTIONS.map((_, i) => (
					<div
						key={i}
						className={`w-2.5 h-2.5 rounded-full transition-all ${
							i === currentQ
								? "bg-purple-400 scale-125"
								: answered[i]
									? "bg-green-400"
									: "bg-muted-foreground/30"
						}`}
					/>
				))}
			</div>
		</div>
	);
}
